import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaBars, FaTimes, FaHome, FaCog, FaUsers, FaQuestionCircle, FaPhone, FaChevronDown, FaHeart, FaBook, FaMusic, FaBible } from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiChartBar, HiBookOpen } from 'react-icons/hi';
import christReignsLogo from '../../assets/crm-newlogo.png';
import LanguageSwitcher from '../common/LanguageSwitcher';

const MobileNavigation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // Prevent body scroll when menu is open
    if (newIsOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.classList.remove('menu-open');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  const scrollToSection = (sectionId: string, pagePath: string = "/software") => {
    if (location.pathname !== pagePath) {
      navigate(pagePath, { state: { scrollTo: sectionId, targetPage: pagePath } });
      closeMenu();
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    closeMenu();
  };

  // Handle scroll after navigation from another page
  useEffect(() => {
    if (location.state && (location.state as any).scrollTo) {
      const sectionId = (location.state as any).scrollTo;
      const targetPage = (location.state as any).targetPage || "/software";
      
      if (location.pathname === targetPage) {
        setTimeout(() => {
          scrollToSection(sectionId, targetPage);
          // Clear state to prevent scrolling again on refresh
          window.history.replaceState({}, document.title);
        }, 100);
      }
    }
  }, [location]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);

      const sections = ['hero', 'how-it-works', 'features', 'dashboard-preview', 'faq', 'our-story', 'cta'];
      const scrollPositionWithOffset = scrollPosition + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPositionWithOffset >= offsetTop && scrollPositionWithOffset < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    {
      id: '/',
      label: t('nav.home'),
      icon: <FaHome className="w-5 h-5" />,
      description: t('mobileNav.descriptions.home'),
      type: 'route'
    },
    {
      id: 'software-menu',
      label: t('nav.software', { defaultValue: "Christ Reigns Lab Software" }),
      icon: <HiSparkles className="w-5 h-5" />,
      description: "Christian Space Dashboard Software",
      subItems: [
        { id: "hero", label: "Overview", type: 'scroll', page: '/software' },
        { id: "how-it-works", label: t('nav.howItWorks'), type: 'scroll', page: '/software' },
        { id: "features", label: t('nav.features'), type: 'scroll', page: '/software' },
        { id: "dashboard-preview", label: t('nav.dashboard'), type: 'scroll', page: '/software' },
        { id: "faq", label: t('nav.faq'), type: 'scroll', page: '/software' },
        { id: "our-story", label: t('nav.ourStory'), type: 'scroll', page: '/software' },
        { id: "/technology", label: t('nav.technology'), type: 'route' }
      ]
    },
    {
      id: '/articles',
      label: t('nav.articles'),
      icon: <FaBook className="w-5 h-5" />,
      description: t('articles.subtitle'),
      type: 'route'
    },
    {
      id: '/bible-and-study-resources',
      label: t('nav.resources', { defaultValue: 'Bible & Study Resources' }),
      icon: <FaBible className="w-5 h-5" />,
      description: t('mobileNav.descriptions.resources', { defaultValue: 'Curated study tools, ministries & courses' }),
      type: 'route'
    },
    {
      id: 'featured-music',
      label: t('nav.music'),
      icon: <FaMusic className="w-5 h-5" />,
      description: t('mobileNav.descriptions.music'),
      type: 'scroll',
      page: '/'
    },
    {
      id: '/about-us',
      label: t('aboutUs.title'),
      icon: <FaHeart className="w-5 h-5" />,
      description: t('mobileNav.descriptions.ourStory'),
      type: 'route'
    }
  ];

  const externalLinks = [
    {
      label: t('mobileNav.external.growthProfile'),
      url: 'https://www.christreignsmedia.io/growth',
      description: t('mobileNav.external.growthProfileDesc'),
      icon: <HiChartBar className="w-4 h-4" />
    },
    {
      label: t('mobileNav.external.bibleChat'),
      url: 'https://www.christreignsmedia.io/bible-and-ai',
      description: t('mobileNav.external.bibleChatDesc'),
      icon: <HiSparkles className="w-4 h-4" />
    },
    {
      label: t('mobileNav.external.bibleProgress'),
      url: 'https://www.christreignsmedia.io/bible-study-progress',
      description: t('mobileNav.external.bibleProgressDesc'),
      icon: <HiLightningBolt className="w-4 h-4" />
    },
    {
      label: t('mobileNav.external.bookLibrary'),
      url: 'https://www.christreignsmedia.io/book-library',
      description: t('mobileNav.external.bookLibraryDesc'),
      icon: <HiBookOpen className="w-4 h-4" />
    },
    {
      label: t('mobileNav.external.favoriteVerses'),
      url: 'https://www.christreignsmedia.io/favorite-verses',
      description: t('mobileNav.external.favoriteVersesDesc'),
      icon: <FaBible className="w-4 h-4" />
    },
    {
      label: t('mobileNav.external.journalBlogs'),
      url: 'https://www.christreignsmedia.io/blogs',
      description: t('mobileNav.external.journalBlogsDesc'),
      icon: <FaBook className="w-4 h-4" />
    }
  ];

  return (
    <>
      {/* Mobile Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 xl:hidden ${
          location.pathname === '/' 
            ? (isScrolled ? "bg-black/80 backdrop-blur-md border-t-4 shadow-lg" : "bg-transparent/20 backdrop-blur-sm border-t-4 border-transparent")
            : "bg-black/80 backdrop-blur-md border-t-4 shadow-lg"
        }`} 
        style={{ borderTopColor: (location.pathname !== '/' || isScrolled) ? '#05a5b7' : 'transparent' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <Link 
            to="/" 
            className="flex items-center"
            onClick={closeMenu}
          >
            <img src={christReignsLogo} alt="Christ Reigns" className="h-8 w-auto object-contain" />
          </Link>
          
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors duration-200 active:scale-95 transform"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 xl:hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={closeMenu}
        />

        {/* Menu Panel */}
        <div
          className={`absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-black/75 backdrop-blur-lg shadow-2xl border-l-4 flex flex-col pt-20 transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ borderLeftColor: '#05a5b7' }}
        >
          {/* Menu Header */}
          {/* <div className="flex items-center justify-between p-6 bg-sky-800/80 backdrop-blur-sm text-white border-b border-sky-700/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-lg">✝️</span>
              </div>
              <div>
                <h2 className="text-lg font-bold">Navigation</h2>
                <p className="text-sky-200 text-sm">Explore Christ Reigns</p>
              </div>
            </div>
            <button
              onClick={closeMenu}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors duration-200"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div> */}

          {/* Menu Content */}
          <div className="flex flex-col flex-grow overflow-y-auto">
            {/* Page Sections */}
            <div className="p-6 space-y-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('mobileNav.pageSections')}
              </h3>
              
              {navigationItems.map((item) => (
                <div key={item.id} className="space-y-1">
                  {item.type === 'route' ? (
                    <Link
                      to={item.id}
                      onClick={closeMenu}
                      className={`w-full flex items-center space-x-4 p-4 rounded-xl transition-all duration-200 group ${
                        location.pathname === item.id
                          ? 'bg-gray-800 border-2 text-white'
                          : 'hover:bg-gray-800/50 border-2 border-transparent text-gray-300'
                      }`}
                      style={(location.pathname === item.id) ? { borderColor: '#05a5b7' } : {}}
                    >
                      <div
                        className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-200 ${
                          location.pathname === item.id
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-800 text-gray-300 group-hover:bg-gray-700'
                        }`}
                      >
                        {item.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white uppercase" style={{ fontFamily: 'Oswald, sans-serif', fontSize: '14px', fontWeight: 400 }}>{item.label}</p>
                        <p className="text-sm opacity-70 text-white">{item.description}</p>
                      </div>
                    </Link>
                  ) : item.type === 'external' ? (
                    <a
                      href={item.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                      className="w-full flex items-center space-x-4 p-4 rounded-xl hover:bg-gray-800/50 border-2 border-transparent text-gray-300 transition-all duration-200 group"
                    >
                      <div className="flex-shrink-0 p-2 rounded-lg bg-gray-800 text-gray-300 group-hover:bg-gray-700 transition-all duration-200">
                        {item.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white uppercase" style={{ fontFamily: 'Oswald, sans-serif', fontSize: '14px', fontWeight: 400 }}>{item.label}</p>
                        <p className="text-sm opacity-70 text-white">{item.description}</p>
                      </div>
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        if (item.subItems) {
                          setExpandedItem(expandedItem === item.id ? null : item.id);
                        } else {
                          scrollToSection(item.id, item.page);
                        }
                      }}
                      className={`w-full flex items-center space-x-4 p-4 rounded-xl transition-all duration-200 group ${
                        activeSection === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeSection))
                          ? 'bg-gray-800 border-2 text-white'
                          : 'hover:bg-gray-800/50 border-2 border-transparent text-gray-300'
                      }`}
                      style={(activeSection === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeSection))) ? { borderColor: '#05a5b7' } : {}}
                    >
                      <div
                        className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-200 ${
                          activeSection === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeSection))
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-800 text-gray-300 group-hover:bg-gray-700'
                        }`}
                      >
                        {item.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white uppercase" style={{ fontFamily: 'Oswald, sans-serif', fontSize: '14px', fontWeight: 400 }}>{item.label}</p>
                        <p className="text-sm opacity-70 text-white">{item.description}</p>
                      </div>
                      {item.subItems ? (
                        <FaChevronDown
                          className={`w-4 h-4 transform transition-transform duration-200 ${
                            expandedItem === item.id ? 'rotate-180 text-gray-400' : 'rotate-0 opacity-40'
                          }`}
                        />
                      ) : (
                        <FaChevronDown
                          className={`w-4 h-4 transform transition-transform duration-200 rotate-270 opacity-40`}
                        />
                      )}
                    </button>
                  )}
                  
                  {/* Nested Submenu */}
                  {item.subItems && expandedItem === item.id && (
                    <div className="pl-14 space-y-1 animate-fadeIn">
                      {item.subItems.map((sub) => (
                        sub.type === 'route' ? (
                          <Link
                            key={sub.id}
                            to={sub.id}
                            onClick={closeMenu}
                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-all transform hover:translate-x-1"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-[#05a5b7]"></div>
                            <span className="font-oswald text-xs uppercase tracking-wider">{sub.label}</span>
                          </Link>
                        ) : (
                          <button
                            key={sub.id}
                            onClick={() => scrollToSection(sub.id, sub.page)}
                            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-all transform hover:translate-x-1"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-[#05a5b7]"></div>
                            <span className="font-oswald text-xs uppercase tracking-wider">{sub.label}</span>
                          </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="px-6">
              <div className="border-t border-gray-800" />
            </div>

            {/* External Links */}
            <div className="p-6 space-y-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('mobileNav.externalLinks')}
              </h3>
              
              {externalLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  className="w-full flex items-center space-x-4 p-4 rounded-xl hover:bg-gray-800 border-2 border-transparent transition-all duration-200 group text-gray-300 hover:text-white"
                >
                  <div className="flex-shrink-0 p-2 rounded-lg bg-gray-800 text-gray-300 group-hover:bg-gray-700 transition-all duration-200">
                    {link.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white uppercase" style={{ fontFamily: 'Oswald, sans-serif', fontSize: '14px', fontWeight: 400 }}>{link.label}</p>
                    <p className="text-sm opacity-70 text-white">{link.description}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-auto p-6 bg-transparent border-t border-gray-800">
              <p className="text-center text-sm text-gray-400">
                © 2025 Christ Reigns Media
              </p>
              <p className="text-center text-xs text-gray-500 mt-1">
                Empowering spiritual growth
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNavigation;